import React, { useState, useMemo } from 'react';
import { Input, Select, SelectItem, DateInput, RadioGroup, Radio, addToast } from '@heroui/react';
import { parseDateTime } from '@internationalized/date';
import { Settings, Plus, Loader2, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from 'lucide-react';
import { Button } from '@/shared/ui/components/Button';
import { cn, formatDate } from '@/shared/lib/utils';
import { logger } from '@/shared/lib';
import { DynamicForm, validateDynamicForm, type JsonSchema, type DynamicFormData } from '@/shared/ui/components/DynamicForm';
import { useTasks, useEnqueueTask } from '@/entities/task';
import { useActionPresets } from '@/entities/action-preset';
import { useLogs } from '@/entities/log';
import type { TaskResponse, TaskLogItem } from '@/shared/api/generated';
import { ActionPresetModal } from './ActionPresetModal';

interface TaskFormData {
  followCount: number;
  interval: number;
  executionType: 'immediate' | 'scheduled';
  scheduledTime?: string;
  platform: string;
  action: string;
  actionPresetId: number | null;
  payloadData: DynamicFormData; // Dynamic form data based on payload_schema
}

export const TaskManagement: React.FC = () => {
  const [formData, setFormData] = useState<TaskFormData>({
    followCount: 10,
    interval: 15,
    executionType: 'immediate',
    platform: '',
    action: '',
    actionPresetId: null,
    payloadData: {},
  });

  // Modal state for action preset management
  const [isActionPresetModalOpen, setIsActionPresetModalOpen] = useState(false);

  // Pagination state for tasks
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Pagination state for logs
  const [logPage, setLogPage] = useState(0);
  const [logPageSize, setLogPageSize] = useState(10);

  // Fetch action presets using actionPresetQueries factory
  const { data: actionPresetsData, isLoading: isLoadingPresets } = useActionPresets({ active: true });

  const actionPresets = actionPresetsData?.data?.action_presets ?? [];

  // Fetch tasks with pagination using taskQueries factory
  const { data: tasksData, isLoading: isLoadingTasks } = useTasks({
    page,
    size: pageSize,
    sortBy: 'id',
    sortDirection: 'DESC',
  });

  const tasks = tasksData?.data?.content ?? [];
  const totalPages = tasksData?.data?.total_pages ?? 0;
  const totalElements = tasksData?.data?.total_elements ?? 0;

  // Fetch logs with pagination using logQueries factory
  const { data: logsData, isLoading: isLoadingLogs } = useLogs({
    page: logPage,
    size: logPageSize,
    sortBy: 'timestamp',
    sortDirection: 'DESC',
  });

  const logs = logsData?.data?.content ?? [];
  const logTotalPages = logsData?.data?.total_pages ?? 0;
  const logTotalElements = logsData?.data?.total_elements ?? 0;

  // Enqueue task mutation
  const { mutate: enqueueTask, isPending: isEnqueuing } = useEnqueueTask();

  // Get unique platforms
  const platforms = useMemo(() => {
    const platformSet = new Set(actionPresets.map(preset => preset.platform));
    return Array.from(platformSet);
  }, [actionPresets]);

  // Get actions for selected platform
  const availableActions = useMemo(() => {
    if (!formData.platform) return [];
    return actionPresets.filter(preset => preset.platform === formData.platform);
  }, [actionPresets, formData.platform]);

  // Get selected preset details
  const selectedPreset = useMemo(() => {
    return actionPresets.find(preset => preset.id === formData.actionPresetId);
  }, [actionPresets, formData.actionPresetId]);

  const handlePayloadDataChange = (payloadData: DynamicFormData) => {
    setFormData(prev => ({ ...prev, payloadData }));
  };

  const handleSubmit = () => {
    // Validate required fields
    if (!formData.platform) {
      addToast({
        title: '플랫폼 선택 필요',
        description: '플랫폼을 선택해주세요.',
        color: 'warning',
      });
      return;
    }

    if (!formData.action || !formData.actionPresetId) {
      addToast({
        title: '작업 타입 선택 필요',
        description: '작업 타입을 선택해주세요.',
        color: 'warning',
      });
      return;
    }

    // Validate dynamic form if schema exists
    if (selectedPreset?.payload_schema) {
      const validation = validateDynamicForm(selectedPreset.payload_schema as unknown as JsonSchema, formData.payloadData);
      if (!validation.isValid) {
        logger.error('Validation errors:', validation.errors);
        addToast({
          title: '입력 항목 확인 필요',
          description: '필수 입력 항목을 확인해주세요:\n' + validation.errors.join('\n'),
          color: 'warning',
        });
        return;
      }
    }

    // Prepare request data matching TaskEnqueueRequest structure
    const requestData = {
      platform: formData.platform,
      action: formData.action,
      scheduled_at: formData.executionType === 'scheduled' ? formData.scheduledTime : undefined,
      payload: formData.payloadData,
    };

    // Enqueue the task
    enqueueTask(
      { body: requestData },
      {
        onSuccess: (response) => {
          logger.info('Task enqueued successfully:', response);
          addToast({
            title: '작업 등록 성공',
            description: `작업이 성공적으로 등록되었습니다. (Task ID: ${response.data?.task_id})`,
            color: 'success',
          });

          // Reset form data
          setFormData({
            followCount: 10,
            interval: 15,
            executionType: 'immediate',
            platform: '',
            action: '',
            actionPresetId: null,
            payloadData: {},
          });

          // Reset to first page - task list will auto-refresh via query invalidation
          setPage(0);
        },
        onError: (error) => {
          logger.error('Failed to enqueue task:', error);
          addToast({
            title: '작업 등록 실패',
            description: '작업 등록에 실패했습니다. 다시 시도해주세요.',
            color: 'danger',
          });
        },
      }
    );
  };

  // Pagination handlers for tasks
  const handlePageChange = (newPage: number) => {
    if (newPage >= 0 && newPage < totalPages) {
      setPage(newPage);
    }
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(0); // Reset to first page when changing page size
  };

  // Pagination handlers for logs
  const handleLogPageChange = (newPage: number) => {
    if (newPage >= 0 && newPage < logTotalPages) {
      setLogPage(newPage);
    }
  };

  const handleLogPageSizeChange = (newSize: number) => {
    setLogPageSize(newSize);
    setLogPage(0); // Reset to first page when changing page size
  };

  // Handler for action preset updates
  const handleActionPresetUpdated = () => {
    // This will trigger a re-fetch of action presets which will update the select options
    // No explicit action needed since useActionPresets will automatically refetch
  };

  return (
    <div className="mb-8 space-y-6">
      {/* 헤더 */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold flex items-center">
            작업 관리
          </h2>
          <p className="text-gray-500 text-sm mt-1">SNS 작업을 관리합니다</p>
        </div>

        <div className="flex space-x-3">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setIsActionPresetModalOpen(true)}
          >
            <Settings className="w-4 h-4 mr-1" /> 작업 관리
          </Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={isEnqueuing || !formData.platform || !formData.actionPresetId}
          >
            {isEnqueuing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
            {isEnqueuing ? '등록 중...' : '작업 등록'}
          </Button>
        </div>
      </div>

      {/* 작업 실행 영역 */}
      <div>
        <h4 className="font-medium mb-4">
          {selectedPreset ? selectedPreset.name : '작업 선택'}
        </h4>
        {selectedPreset?.description && (
          <p className="text-sm text-gray-500 mb-4">{selectedPreset.description}</p>
        )}

        <form className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Platform Selection */}
            <div>
              <Select
                label="플랫폼 선택"
                placeholder="플랫폼을 선택하세요"
                selectedKeys={formData.platform ? [formData.platform] : []}
                onSelectionChange={(keys) => {
                  const selectedPlatform = Array.from(keys)[0] as string;
                  setFormData(prev => ({
                    ...prev,
                    platform: selectedPlatform || '',
                    action: '',
                    actionPresetId: null,
                  }));
                }}
                isDisabled={isLoadingPresets}
                classNames={{
                  trigger: "bg-white border border-gray-300"
                }}
              >
                {platforms.map((platform) => (
                  <SelectItem key={platform}>
                    {platform.toUpperCase()}
                  </SelectItem>
                ))}
              </Select>
            </div>

            {/* Action Selection */}
            <div>
              <Select
                label="작업 타입 선택"
                placeholder="작업 타입을 선택하세요"
                selectedKeys={formData.actionPresetId ? [String(formData.actionPresetId)] : []}
                onSelectionChange={(keys) => {
                  const presetId = Array.from(keys)[0] ? Number(Array.from(keys)[0]) : null;
                  const preset = actionPresets.find(p => p.id === presetId);
                  setFormData(prev => ({
                    ...prev,
                    actionPresetId: presetId,
                    action: preset?.action ?? '',
                    payloadData: {}, // Reset payload data when changing preset
                  }));
                }}
                isDisabled={!formData.platform || isLoadingPresets}
                classNames={{
                  trigger: "bg-white border border-gray-300"
                }}
              >
                {availableActions.map((preset) => (
                  <SelectItem key={String(preset.id)}>
                    {preset.name}
                  </SelectItem>
                ))}
              </Select>
            </div>
            
            <div>
              <Input
                type="number"
                label="작업 수량"
                value={String(formData.followCount)}
                onChange={(e) => setFormData(prev => ({ ...prev, followCount: parseInt(e.target.value) || 0 }))}
                min={1}
                max={100}
                classNames={{
                  input: "bg-white",
                  inputWrapper: "bg-white border border-gray-300"
                }}
              />
            </div>

            <div>
              <Input
                type="number"
                label="작업 시간 간격 (초)"
                value={String(formData.interval)}
                onChange={(e) => setFormData(prev => ({ ...prev, interval: parseInt(e.target.value) || 0 }))}
                placeholder="작업 간격을 초 단위로 입력하세요"
                min={1}
                max={300}
                classNames={{
                  input: "bg-white",
                  inputWrapper: "bg-white border border-gray-300"
                }}
              />
            </div>

            <div>
              <RadioGroup
                label="실행 방식"
                orientation="horizontal"
                value={formData.executionType}
                onValueChange={(value) => setFormData(prev => ({ ...prev, executionType: value as 'immediate' | 'scheduled' }))}
              >
                <Radio value="immediate">즉시 실행</Radio>
                <Radio value="scheduled">실행 시간 설정</Radio>
              </RadioGroup>

              {formData.executionType === 'scheduled' && (
                <div className="mt-2 animate-fadeIn">
                  <DateInput
                    label="실행 시간"
                    hideTimeZone
                    granularity="minute"
                    value={formData.scheduledTime ? parseDateTime(formData.scheduledTime) : null}
                    onChange={(value) => {
                      if (value) {
                        // Convert DateValue to ISO string format (YYYY-MM-DDTHH:mm)
                        const isoString = `${value.year}-${String(value.month).padStart(2, '0')}-${String(value.day).padStart(2, '0')}T${String(value.hour).padStart(2, '0')}:${String(value.minute).padStart(2, '0')}`;
                        setFormData(prev => ({ ...prev, scheduledTime: isoString }));
                      } else {
                        setFormData(prev => ({ ...prev, scheduledTime: undefined }));
                      }
                    }}
                    classNames={{
                      input: "bg-white",
                      inputWrapper: "bg-white border border-gray-300"
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Dynamic Form Section - Based on payload_schema */}
          {selectedPreset?.payload_schema && (
            <div className="border-t border-gray-200 pt-6">
              <h5 className="text-sm font-medium text-gray-700 mb-4">
                작업 상세 설정
              </h5>
              <DynamicForm
                schema={selectedPreset.payload_schema as unknown as JsonSchema}
                data={formData.payloadData}
                onChange={handlePayloadDataChange}
                disabled={false}
              />
            </div>
          )}
        </form>
      </div>

      {/* 채널 계정 관리 섹션 */}
      <div className="py-6">
        <h4 className="font-medium mb-4">작업 목록</h4>

        {isLoadingTasks ? (
          <div className="text-center py-8 text-gray-500 px-6">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            로딩 중...
          </div>
        ) : tasks.length === 0 ? (
          <div className="text-center py-8 text-gray-500 px-6">
            등록된 작업이 없습니다.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">플랫폼</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">작업 타입</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">상태</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">예약 시간</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">시작 시간</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">완료 시간</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">장치 ID</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {tasks.map((task: TaskResponse) => (
                  <tr key={task.id} className="hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4 text-sm font-medium">{task.id}</td>
                    <td className="py-3 px-4 text-sm">
                      <span className="inline-flex items-center px-2 py-1 rounded bg-gray-100 text-gray-700 text-xs font-medium uppercase">
                        {task.platform}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm">{task.action}</td>
                    <td className="py-3 px-4">
                      <span className={cn(
                        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                        task.status === 'PENDING' ? 'bg-warning/10 text-warning' :
                        task.status === 'CLAIMED' ? 'bg-primary/10 text-primary' :
                        task.status === 'COMPLETED' ? 'bg-success/10 text-success' :
                        task.status === 'FAILED' ? 'bg-danger/10 text-danger' :
                        'bg-gray-100 text-gray-600'
                      )}>
                        <span className={cn('w-2 h-2 rounded-full mr-1',
                          task.status === 'PENDING' ? 'bg-warning' :
                          task.status === 'CLAIMED' ? 'bg-primary' :
                          task.status === 'COMPLETED' ? 'bg-success' :
                          task.status === 'FAILED' ? 'bg-danger' :
                          'bg-gray-400'
                        )}></span>
                        {task.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-500">
                      {task.scheduled_at ? formatDate(task.scheduled_at) : '-'}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-500">
                      {task.claimed_at ? formatDate(task.claimed_at) : '-'}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-500">
                      {task.completed_at ? formatDate(task.completed_at) : '-'}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-500">
                      {task.claimed_by_device_id || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {!isLoadingTasks && tasks.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-4 border-t border-gray-200">
            {/* Page info and size selector */}
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-600">
                전체 <span className="font-medium">{totalElements}</span>개 중{' '}
                <span className="font-medium">{page * pageSize + 1}</span>-
                <span className="font-medium">{Math.min((page + 1) * pageSize, totalElements)}</span> 표시
              </div>

              <Select
                selectedKeys={[String(pageSize)]}
                onSelectionChange={(keys) => handlePageSizeChange(Number(Array.from(keys)[0]))}
                classNames={{
                  trigger: "bg-white border border-gray-300 h-8 min-h-8",
                  value: "text-sm"
                }}
                className="w-28"
                aria-label="페이지 크기 선택"
              >
                <SelectItem key="5">5개씩</SelectItem>
                <SelectItem key="10">10개씩</SelectItem>
                <SelectItem key="20">20개씩</SelectItem>
                <SelectItem key="50">50개씩</SelectItem>
              </Select>
            </div>

            {/* Page navigation */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(0)}
                disabled={page === 0}
              >
                <ChevronsLeft className="w-4 h-4" />
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 0}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>

              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  // Show current page and 2 pages before/after
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i;
                  } else if (page < 3) {
                    pageNum = i;
                  } else if (page > totalPages - 3) {
                    pageNum = totalPages - 5 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={cn(
                        'min-w-[32px] h-8 px-2 text-sm rounded transition-colors',
                        page === pageNum
                          ? 'bg-primary text-white font-medium'
                          : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                      )}
                    >
                      {pageNum + 1}
                    </button>
                  );
                })}
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= totalPages - 1}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(totalPages - 1)}
                disabled={page >= totalPages - 1}
              >
                <ChevronsRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* 작업 로그 */}
      <div className="py-6 border-t border-gray-100">
        <h4 className="font-medium mb-4">작업 로그</h4>

        {isLoadingLogs ? (
          <div className="text-center py-8 text-gray-500 px-6">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            로딩 중...
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-8 text-gray-500 px-6">
            작업 로그가 없습니다.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">플랫폼</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">로그 레벨</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">실행 시간</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">코멘트</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {logs.map((log: TaskLogItem, index: number) => (
                  <tr key={`${log.platform}-${log.timestamp}-${index}`} className="hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4 text-sm">
                      <span className="inline-flex items-center px-2 py-1 rounded bg-gray-100 text-gray-700 text-xs font-medium uppercase">
                        {log.platform}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={cn(
                        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                        log.log_level === 'ERROR' ? 'bg-danger/10 text-danger' :
                        log.log_level === 'WARN' ? 'bg-warning/10 text-warning' :
                        log.log_level === 'INFO' ? 'bg-primary/10 text-primary' :
                        log.log_level === 'DEBUG' ? 'bg-gray-100 text-gray-600' :
                        'bg-gray-100 text-gray-600'
                      )}>
                        <span className={cn('w-2 h-2 rounded-full mr-1',
                          log.log_level === 'ERROR' ? 'bg-danger' :
                          log.log_level === 'WARN' ? 'bg-warning' :
                          log.log_level === 'INFO' ? 'bg-primary' :
                          log.log_level === 'DEBUG' ? 'bg-gray-400' :
                          'bg-gray-400'
                        )}></span>
                        {log.log_level || 'INFO'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-500">
                      {formatDate(log.timestamp)}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-500 max-w-md truncate">
                      {log.comment || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls for Logs */}
        {!isLoadingLogs && logs.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-4 border-t border-gray-200">
            {/* Page info and size selector */}
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-600">
                전체 <span className="font-medium">{logTotalElements}</span>개 중{' '}
                <span className="font-medium">{logPage * logPageSize + 1}</span>-
                <span className="font-medium">{Math.min((logPage + 1) * logPageSize, logTotalElements)}</span> 표시
              </div>

              <Select
                selectedKeys={[String(logPageSize)]}
                onSelectionChange={(keys) => handleLogPageSizeChange(Number(Array.from(keys)[0]))}
                classNames={{
                  trigger: "bg-white border border-gray-300 h-8 min-h-8",
                  value: "text-sm"
                }}
                className="w-28"
                aria-label="로그 페이지 크기 선택"
              >
                <SelectItem key="5">5개씩</SelectItem>
                <SelectItem key="10">10개씩</SelectItem>
                <SelectItem key="20">20개씩</SelectItem>
                <SelectItem key="50">50개씩</SelectItem>
              </Select>
            </div>

            {/* Page navigation */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleLogPageChange(0)}
                disabled={logPage === 0}
              >
                <ChevronsLeft className="w-4 h-4" />
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handleLogPageChange(logPage - 1)}
                disabled={logPage === 0}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>

              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, logTotalPages) }, (_, i) => {
                  let pageNum: number;
                  if (logTotalPages <= 5) {
                    pageNum = i;
                  } else if (logPage < 3) {
                    pageNum = i;
                  } else if (logPage > logTotalPages - 3) {
                    pageNum = logTotalPages - 5 + i;
                  } else {
                    pageNum = logPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handleLogPageChange(pageNum)}
                      className={cn(
                        'min-w-[32px] h-8 px-2 text-sm rounded transition-colors',
                        logPage === pageNum
                          ? 'bg-primary text-white font-medium'
                          : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                      )}
                    >
                      {pageNum + 1}
                    </button>
                  );
                })}
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handleLogPageChange(logPage + 1)}
                disabled={logPage >= logTotalPages - 1}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handleLogPageChange(logTotalPages - 1)}
                disabled={logPage >= logTotalPages - 1}
              >
                <ChevronsRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Action Preset Management Modal */}
      <ActionPresetModal
        isOpen={isActionPresetModalOpen}
        onClose={() => setIsActionPresetModalOpen(false)}
        onActionPresetUpdated={handleActionPresetUpdated}
      />
    </div>
  );
};